import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { 
  Calendar, Clock, ArrowLeft, Share2, Bookmark, Heart,
  Facebook, Twitter, Linkedin, Copy, Check, ChevronRight
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { BlogPost } from '../types/database';
import toast from 'react-hot-toast';

const BlogArticle: React.FC = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState<BlogPost | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);

  useEffect(() => {
    loadArticle();
  }, [slug]);

  const loadArticle = async () => {
    try {
      if (!slug) return;
      
      setLoading(true);
      const { data: article, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error) throw error;
      
      if (article) {
        setArticle(article);
        document.title = `${article.title} | Proxy Emobility`;
        
        // Load related articles from the same category
        const { data: related } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('category', article.category)
          .neq('id', article.id)
          .limit(3);

        setRelatedArticles(related || []);
      }
    } catch (error) {
      console.error('Error loading article:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleShare = async (platform?: string) => {
    const url = window.location.href;
    const title = article?.title || '';

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${url}&text=${title}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}`, '_blank');
        break;
      default:
        try {
          await navigator.clipboard.writeText(url);
          setCopied(true);
          toast.success('Lien copié !');
          setTimeout(() => setCopied(false), 2000);
        } catch (err) {
          toast.error('Impossible de copier le lien');
        }
    }
  };

  const handleLike = () => {
    setLiked(!liked);
    toast.success(liked ? 'Like retiré' : 'Article liké !');
  };

  const handleBookmark = () => {
    setBookmarked(!bookmarked);
    toast.success(bookmarked ? 'Favori retiré' : 'Article ajouté aux favoris !');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 w-32 bg-gray-200 rounded mb-8"></div>
              <div className="h-12 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-8"></div>
              <div className="aspect-w-16 aspect-h-9 mb-8">
                <div className="w-full h-full bg-gray-200 rounded-xl"></div>
              </div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                <div className="h-4 bg-gray-200 rounded w-4/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Article non trouvé</h2>
            <Link 
              to="/blog" 
              className="text-primary-600 hover:text-primary-700 font-medium inline-flex items-center"
            >
              <ArrowLeft size={20} className="mr-2" />
              Retour aux articles
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <Helmet>
        <title>{article.title} | Proxy Emobility</title>
        <meta name="description" content={article.excerpt} />
        <meta name="keywords" content={`${article.category}, mobilité électrique, Proxy Emobility, Afrique, ${article.title.toLowerCase()}`} />
        <link rel="canonical" href={`https://proxyemobility.netlify.app/blog/${article.slug}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://proxyemobility.netlify.app/blog/${article.slug}`} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:image" content={article.cover_image} />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`https://proxyemobility.netlify.app/blog/${article.slug}`} />
        <meta property="twitter:title" content={article.title} />
        <meta property="twitter:description" content={article.excerpt} />
        <meta property="twitter:image" content={article.cover_image} />
        
        {/* Schema.org structured data for Article */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": "${article.title}",
              "description": "${article.excerpt}",
              "image": "${article.cover_image}",
              "author": {
                "@type": "Person",
                "name": "${article.author.name}"
              },
              "publisher": {
                "@type": "Organization",
                "name": "Proxy Emobility",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://proxyemobility.netlify.app/proxy-logo.png"
                }
              },
              "datePublished": "${article.created_at}",
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "https://proxyemobility.netlify.app/blog/${article.slug}"
              }
            }
          `}
        </script>
      </Helmet>
      
      {/* Article Header */}
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <Link 
              to="/blog"
              className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-8"
            >
              <ArrowLeft size={20} className="mr-2" />
              Retour aux articles
            </Link>

            <div className="flex items-center gap-4 text-sm text-gray-600 mb-6">
              <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full font-medium">
                {article.category}
              </span>
              <span className="flex items-center">
                <Calendar size={16} className="mr-1" />
                {formatDate(article.created_at)}
              </span>
              <span className="flex items-center">
                <Clock size={16} className="mr-1" />
                {article.read_time} min
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {article.title}
            </h1>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <img
                  src={article.author.avatar}
                  alt={article.author.name}
                  className="w-12 h-12 rounded-full object-cover mr-4"
                />
                <div>
                  <p className="font-medium text-gray-900">{article.author.name}</p>
                  <p className="text-sm text-gray-600">Auteur</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleLike}
                  className={`p-2 rounded-full transition-colors ${
                    liked 
                      ? 'bg-red-100 text-red-500' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Heart size={20} className={liked ? 'fill-current' : ''} />
                </button>
                <button
                  onClick={handleBookmark}
                  className={`p-2 rounded-full transition-colors ${
                    bookmarked 
                      ? 'bg-primary-100 text-primary-600' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Bookmark size={20} className={bookmarked ? 'fill-current' : ''} />
                </button>
                <button
                  onClick={() => handleShare()}
                  className="p-2 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors"
                >
                  {copied ? <Check size={20} /> : <Share2 size={20} />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Cover Image */}
            <div className="aspect-w-16 aspect-h-9 mb-12">
              <img
                src={article.cover_image}
                alt={article.title}
                className="w-full h-full object-cover rounded-xl"
              />
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              <div dangerouslySetInnerHTML={{ __html: article.content }} />
            </div>

            {/* Share Section */}
            <div className="border-t border-gray-200 mt-12 pt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Partager cet article
              </h3>
              <div className="flex gap-3">
                <button
                  onClick={() => handleShare('facebook')}
                  className="p-3 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors"
                >
                  <Facebook size={20} />
                </button>
                <button
                  onClick={() => handleShare('twitter')}
                  className="p-3 bg-sky-500 text-white rounded-full hover:bg-sky-600 transition-colors"
                >
                  <Twitter size={20} />
                </button>
                <button
                  onClick={() => handleShare('linkedin')}
                  className="p-3 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors"
                >
                  <Linkedin size={20} />
                </button>
                <button
                  onClick={() => handleShare()}
                  className="p-3 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors"
                >
                  {copied ? <Check size={20} /> : <Copy size={20} />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="bg-white py-12 border-t">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-8">
                Articles Similaires
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/blog/${article.slug}`}
                    className="group"
                  >
                    <div className="aspect-w-16 aspect-h-9 mb-4">
                      <img
                        src={article.cover_image}
                        alt={article.title}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <span className="inline-flex items-center text-primary-600 text-sm font-medium mt-2 group-hover:translate-x-1 transition-transform">
                      Lire l'article
                      <ChevronRight size={16} className="ml-1" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogArticle;